import { FC, useCallback, useState } from 'react'
import { MyDialog } from 'components/MyDialog'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getUserData } from 'redux/reducers/users'
import { Alert, Accordion, AccordionItem } from '@cmsgov/ds-medicare-gov'
import { Button } from 'components/Button/Button'
import { medicareApiClient } from 'clients/medicareApiClient'
import { ILoadingStatus } from 'models/LoadingStatus'
import { useMountedState } from 'hooks/useMountedState'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { MyLink } from 'components/MyLink'
import { Activity, Component } from '@cms-max/medicare-api-sdk'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'

export type OrderMedicareCardModalProps = {
  onClose: () => void
}

export const OrderMedicareCardModal:FC<OrderMedicareCardModalProps> = ({ onClose }) => {
  const { t } = useTranslation('orderMedicareCard')
  const userData = useSelector(getUserData)
  const [loadingState, setLoadingState] = useMountedState<ILoadingStatus>('standby')
  const [isControlledOpen, setIsControlledOpen] = useState<boolean>(false)

  const orderNewCard = async () => {
    setLoadingState('loading')
    try {
      const response = await medicareApiClient.asyncPostBeneficiaryMedicareCard()
      if (response?.status === 204) {
        setLoadingState('finished')
      } else {
        setLoadingState('error')
      }
    } catch (error) {
      setLoadingState('error')
    }
  }

  const openLiveChat = () => {
    if (window.chTriggers?.openHeaderModal) {
      onClose()
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      //  Race condition with DropDownTrigger on Global-Header
      // Also set delay so user can see where the dropdown happens at
      setTimeout(() => {
        window.chTriggers?.openHeaderModal('Chat')
      }, 300)
    }
  }

  const accordionOnChange = useCallback(() => {
    window.globalHeaderEvents?.handleEvent?.({
      core: {
        property: 'MAX',
        activity: isControlledOpen ? Activity.CloseAccordion : Activity.OpenAccordion,
        component: Component.AccountSettings,
        elementId: isControlledOpen ? 'accountsettings_ordercardchangeaddress_accordionclose' : 'accountsettings_ordercardchangeaddress_accordionopen'
      }
    })
    setIsControlledOpen(!isControlledOpen)
  }, [isControlledOpen, setIsControlledOpen])

  const handleClose = useModalCloseEvent('accountSettings', 'accountsettings_ordercardclose_click', onClose)

  return (
    <MyDialog
      heading={t('heading')}
      containerId='medicare-my-order-medicare-card-modal'
      closeButtonAriaLabel={t('Close')}
      onClose={handleClose}
    >
      <>
        {loadingState !== 'finished' && (
          <>
            <p>{t('para')}{' '}
              <Button
                className="ds-c-button ds-c-button--ghost ds-u-padding--0 modal-blue my-u-font-size--16"
                onClick={openLiveChat}
                siteActivityProps={{
                  core: {
                    component: Component.AccountSettings,
                    elementId: 'accountsettings_ordercardchat_click',
                    coreClickData: {
                      linkText: t('liveChat')
                    }
                  }
                }}
              >{t('liveChat')}</Button>
              {' '}{t('para2')}
            </p>
            <h3>{t('mailTitle')}</h3>
            <p className="my-u-mb--0">{userData?.user?.mailingAddress?.address1} {userData?.user?.mailingAddress?.city},</p>
            <p>{userData?.user?.mailingAddress?.state} {userData?.user?.mailingAddress?.zip}</p>
            <Alert hideIcon className="my-u-mb--14 my-u-pl--4">
              <Accordion className="my-c-alert-accordion">
                <AccordionItem
                  heading={t('accordion.title')}
                  contentClassName="my-c-alert-accordion-item"
                  isControlledOpen={isControlledOpen}
                  onChange={accordionOnChange}
                >
                  <p className="ds-c-alert__text">{t('accordion.para')}</p>
                  <ul className="ds-c-alert__text my-u-pl--12">
                    <li>
                      <MyLink
                        href="https://www.ssa.gov/myaccount/"
                        target='_blank'
                        rel="noopener noreferrer"
                        siteActivityProps={{
                          core: {
                            component: Component.AccountSettings,
                            elementId: 'accountsettings_ordercardssupdatenameoraddress_click',
                            coreClickData: {
                              linkText: t('accordion.link1'),
                              linkUrl: 'https://www.ssa.gov/myaccount/'
                            }
                          }
                        }} >{t('accordion.link1')}</MyLink> {t('accordion.li1')}
                    </li>
                    <li>{t('accordion.li2')}</li>
                    <li>
                    <MyLink
                        href="https://www.ssa.gov/locator/"
                        target='_blank'
                        rel="noopener noreferrer"
                        siteActivityProps={{
                          core: {
                            component: Component.AccountSettings,
                            elementId: 'accountsettings_ordercardssofficefinder_click',
                            coreClickData: {
                              linkText: t('accordion.link3'),
                              linkUrl: 'https://www.ssa.gov/myaccount/'
                            }
                          }
                        }}
                      >{t('accordion.link3')}</MyLink> {t('accordion.li3')}
                    </li>
                  </ul>
                </AccordionItem>

              </Accordion>
            </Alert>
            {loadingState === 'error' && (
              <Alert variation='error' className="my-u-mb--14 my-u-pl--4">
                <p aria-live="polite" className="ds-c-alert__text">{t('errorMessage')}</p>
              </Alert>
            )}
            <Button
              className="ds-c-button ds-c-button--solid my-u-py--6 my-u-mr--6"
              onClick={orderNewCard}
              siteActivityProps={{
                core: {
                  component: Component.AccountSettings,
                  elementId: 'accountsettings_ordercardconfirm_click',
                  coreClickData: {
                    linkText: t('btn.order')
                  }
                }
              }}
            >
              {loadingState === 'loading' && <MySpinner size="small" data-testid='loading-spinner' aria-valuetext='Next' />}
              {t('btn.order')}
            </Button>
            <Button className="ds-c-button my-u-py--6" onClick={onClose}>
              {t('btn.cancel')}
            </Button>
          </>
        )}
        {loadingState === 'finished' && (
          <>
            <Alert variation='success' hideIcon className="my-u-mb--14 my-u-pl--4">
              {t('success.alert')}
            </Alert>
            <p>{t('success.para')}</p>
            <Button className="ds-c-button ds-c-button--solid my-u-py--6" onClick={onClose}>
              {t('Close')}
            </Button>
          </>
        )}
      </>
    </MyDialog>
  )
}
